/**
 * Analysis History Manager
 * Manages history of analysis operations
 *
 * @module AnalysisHistory
 * Sprint 32: OOA/OOD Refactoring
 */

import { AnalysisHistoryEntry } from '../types/ii-agent-types';

/**
 * Interface for analysis history management
 */
export interface IAnalysisHistory {
    add(entry: AnalysisHistoryEntry): void;
    getAll(): AnalysisHistoryEntry[];
    getByType(type: string): AnalysisHistoryEntry[];
    getLatest(type?: string): AnalysisHistoryEntry | undefined;
    clear(): void;
}

/**
 * Analysis history manager class
 */
export class AnalysisHistory implements IAnalysisHistory {
    private entries: AnalysisHistoryEntry[] = [];

    /**
     * Add an entry to the history
     */
    add(entry: AnalysisHistoryEntry): void {
        this.entries.push(entry);
    }

    /**
     * Get all history entries
     */
    getAll(): AnalysisHistoryEntry[] {
        return [...this.entries];
    }

    /**
     * Get entries filtered by type
     */
    getByType(type: string): AnalysisHistoryEntry[] {
        return this.entries.filter(e => e.type === type);
    }

    /**
     * Get the most recent entry, optionally filtered by type
     */
    getLatest(type?: string): AnalysisHistoryEntry | undefined {
        const filtered = type ? this.getByType(type) : this.entries;
        return filtered[filtered.length - 1];
    }

    /**
     * Clear all history entries
     */
    clear(): void {
        this.entries = [];
    }

    /**
     * Get count of entries
     */
    getCount(): number {
        return this.entries.length;
    }

    /**
     * Get count by type
     */
    getCountByType(type: string): number {
        return this.getByType(type).length;
    }
}
